import { useCallback, useState } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import { logError } from "@entrilia/shared/logging.ts";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type { AnyObject } from "@entrilia/shared/types.ts";
import type {
  AddReportGroupRequest,
  CreateReportGroup,
  DeleteReportGroupResult,
  GetReportAuthorizationResponse,
  ReorderReportGroupsRequest,
  ReplaceReportGroupRequest,
  ReportAuthorization,
  ReportGroup,
  ReportGroupsResponse,
  ReportInfo,
  SharedWithEntity,
  UpdateReportGroupAuthorizationRequest,
  UpdateReportGroupRequest,
} from "../../../../../../api/types/svTypes.ts";

interface Props {
  getAccessibleReportGroups: (clientCode: string) => Promise<ApiResponse<ReportGroupsResponse>>;
  getAllReportGroups?: (clientCode: string) => Promise<ApiResponse<ReportGroupsResponse>>;
  addReportGroup: (request: AddReportGroupRequest) => Promise<ApiResponse<ReportGroup>>;
  updateReportGroup: (request: UpdateReportGroupRequest) => Promise<ApiResponse<ReportGroup>>;
  deleteReportGroup: (
    clientCode: string,
    groupId: string,
    reassignToGroupId?: string
  ) => Promise<ApiResponse<DeleteReportGroupResult>>;
  replaceReportGroup?: (request: ReplaceReportGroupRequest) => Promise<ApiResponse<AnyObject>>;
  reorderReportGroups: (request: ReorderReportGroupsRequest) => Promise<ApiResponse<ReportGroupsResponse>>;
  addReportGroups?: (groups: CreateReportGroup[]) => Promise<ApiResponse<ReportInfo[]>>;
  getReportGroupSharing?: (clientCode: string, groupId: string) => Promise<ApiResponse<GetReportAuthorizationResponse>>;
  updateReportGroupSharing?: (
    clientCode: string,
    groupId: string,
    request: UpdateReportGroupAuthorizationRequest
  ) => Promise<ApiResponse<ReportGroup>>;
  // Applies a group's new sharing to the loaded reports that inherit from it (derives their display sharing from
  // the group's sharedWith, no reload). Note: this updates the visible sharing (icons), not the caller's effective
  // access level, which is a server-resolved permission.
  applyGroupSharing?: (groupId: string, sharedWith: SharedWithEntity[]) => void;
  getOrganizationReportGroups?: (signal?: AbortSignal) => Promise<ApiResponse<Record<string, ReportGroup[]>>>;
}

/**
 * Owns the per-client report-group cache (`groups`, keyed by client code) plus the CRUD/sharing actions.
 *
 * Fetching comes in three flavours, deliberately distinct:
 *  - `load` / `loadOrganizationGroups` — fetch into the cache and flip the `loaded` flag (initial page load).
 *  - `refreshClientGroups` (internal) — silently re-fetch one client after a mutation; `loaded` is already set.
 *  - `fetchAllReportGroups` — fetch every group on the client, unfiltered by the caller's access, and return it WITHOUT
 *    touching the cache. Backs the copy-report target picker, where a report may be placed into any existing folder.
 */
export default function useGroups({
  getAccessibleReportGroups,
  getAllReportGroups,
  addReportGroup,
  updateReportGroup,
  deleteReportGroup,
  replaceReportGroup,
  reorderReportGroups,
  addReportGroups,
  getReportGroupSharing,
  updateReportGroupSharing,
  applyGroupSharing,
  getOrganizationReportGroups,
}: Props) {
  const [groups, setGroups] = useState<Record<string, ReportGroup[]>>({});
  const [error, setError] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState<boolean>(false);

  // Writes one client's groups into the cache, leaving the other clients untouched.
  const setClientGroups = useCallback((clientCode: string, clientGroups: ReportGroup[]) => {
    setGroups((prev) => ({ ...prev, [clientCode]: clientGroups }));
  }, []);

  // Silently re-fetch a single client's groups after a mutation (add / update / delete / rename / reorder).
  // The `loaded`/`error` flags are owned by the action that triggered it, so this stays quiet on failure.
  const refreshClientGroups = useCallback(
    async (clientCode: string) => {
      const resp = await getAccessibleReportGroups(clientCode);
      if (resp.success) {
        setClientGroups(clientCode, resp.data);
      }
    },
    [getAccessibleReportGroups, setClientGroups]
  );

  // Initial load of a single client's groups into the cache (drives the page's `loaded` flag).
  const load = useCallback(
    async (clientCode: string) => {
      try {
        const resp = await getAccessibleReportGroups(clientCode);
        if (!resp.success) {
          setError(resp.error?.message);
          return;
        }
        setClientGroups(clientCode, resp.data);
      } catch {
        setError("Report groups loading failed");
      } finally {
        setLoaded(true);
      }
    },
    [getAccessibleReportGroups, setClientGroups]
  );

  // Initial load of groups (with sharing) for every client of the organization in one call.
  // No-ops when the bulk fetch isn't provided (single-client pages don't pass it).
  const loadOrganizationGroups = useCallback(async () => {
    if (!getOrganizationReportGroups) {
      return;
    }
    try {
      const resp = await getOrganizationReportGroups();
      if (!resp.success) {
        setError(resp.error?.message);
        return;
      }
      setGroups(resp.data);
    } catch {
      setError("Report groups loading failed");
    } finally {
      setLoaded(true);
    }
  }, [getOrganizationReportGroups]);

  const fetchAllReportGroups = useCallback(
    async (clientCode: string) => {
      const resp = await defined(getAllReportGroups)(clientCode);
      if (resp.success) {
        return resp.data;
      }
      logError(resp.error, "[useGroups] fetchAllReportGroups");
      return undefined;
    },
    [getAllReportGroups]
  );

  const add = useCallback(
    async (clientCode: string, group: ReportGroup) => {
      try {
        const resp = await addReportGroup({ clientCode, group });
        if (resp.success) {
          await refreshClientGroups(clientCode);
          return resp.data;
        }
        setError(resp.error?.message);
        return undefined;
      } catch {
        setError("Report groups adding failed");
        return undefined;
      } finally {
        setLoaded(true);
      }
    },
    [addReportGroup, refreshClientGroups]
  );

  const update = useCallback(
    async (clientCode: string, group: ReportGroup) => {
      try {
        const resp = await updateReportGroup({ clientCode, group });
        if (resp.success) {
          await refreshClientGroups(clientCode);
          return;
        }
        setError(resp.error?.message);
      } catch {
        setError("Report groups updating failed");
      } finally {
        setLoaded(true);
      }
    },
    [updateReportGroup, refreshClientGroups]
  );

  const remove = useCallback(
    async (clientCode: string, groupId: string, reassignToGroupId?: string) => {
      try {
        const resp = await deleteReportGroup(clientCode, groupId, reassignToGroupId);
        if (!resp.success) {
          setError(resp.error?.message);
          throw new Error(resp.error?.message);
        }
        if (resp.data.status === "Deleted") {
          await refreshClientGroups(clientCode);
        }
        return resp.data;
      } finally {
        setLoaded(true);
      }
    },
    [deleteReportGroup, refreshClientGroups]
  );

  const replace = useCallback(
    async (clientCode: string, fromGroup: string, toGroup: string, reloadReports: () => Promise<void>) => {
      const resp = await defined(replaceReportGroup)({ clientCode, fromGroup, toGroup });
      if (resp.success) {
        await reloadReports();
        return resp.data;
      }
      throw new Error(resp.error?.message);
    },
    [replaceReportGroup]
  );

  const reorder = useCallback(
    async (clientCode: string, clientGroups: ReportGroup[]) => {
      const resp = await reorderReportGroups({ clientCode, groups: clientGroups });
      if (!resp.success) {
        throw new Error(resp.error?.message);
      }
      setClientGroups(clientCode, resp.data);
    },
    [reorderReportGroups, setClientGroups]
  );

  const addMany = useCallback(
    async (groupsToCreate: CreateReportGroup[]) => {
      const resp = await defined(addReportGroups)(groupsToCreate);
      if (resp.success) {
        return resp.data;
      }
      logError(resp.error, "[useGroups] addMany");
      return undefined;
    },
    [addReportGroups]
  );

  const getSharing = useCallback(
    (clientCode: string, groupId: string) => defined(getReportGroupSharing)(clientCode, groupId),
    [getReportGroupSharing]
  );

  const updateSharing = useCallback(
    async (clientCode: string, groupId: string, authorization: ReportAuthorization) => {
      const resp = await defined(updateReportGroupSharing)(clientCode, groupId, { authorization });
      // Patch the affected group's recomputed SharedWith into the cache (mirrors how report sharing patches
      // ReportAuthorizationInfo) so the grid folder row updates without refetching the whole list.
      if (resp.success && resp.data) {
        const sharedWith = resp.data.sharedWith ?? [];
        setGroups((prev) => {
          const clientGroups = prev[clientCode];
          if (!clientGroups) {
            return prev;
          }
          return {
            ...prev,
            [clientCode]: clientGroups.map((g) => (g.id === groupId ? { ...g, sharedWith } : g)),
          };
        });
        // Reports that inherit from this group show the group's sharing; update their icons from the same data.
        applyGroupSharing?.(groupId, sharedWith);
      }
      return resp;
    },
    [updateReportGroupSharing, applyGroupSharing]
  );

  return {
    ui: {
      error,
      loaded,
    },
    actions: {
      load,
      loadOrganizationGroups,
      reorder,
      delete: remove,
      add,
      update,
      replace: replaceReportGroup ? replace : undefined,
      fetchAllReportGroups: getAllReportGroups ? fetchAllReportGroups : undefined,
      addMany: addReportGroups ? addMany : undefined,
      getSharing: getReportGroupSharing ? getSharing : undefined,
      updateSharing: updateReportGroupSharing ? updateSharing : undefined,
    },
    groups,
  };
}
